import React from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { LogEntry, LogLevel } from '../types'
import { Translate } from '../useTranslate'
import { formatSubsystem } from '../../../utils/utils'
import useStyles from '../styles'
import LogMessageCell from './LogMessageCell'
import LogUserCell from './LogUserCell'

type Classes = ReturnType<typeof useStyles>

const EM_DASH = '-'

export function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number, len = 2): string => String(n).padStart(len, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(
    d.getMilliseconds(),
    3,
  )}`
}

function getPayloadField(entry: LogEntry, key: string): string {
  const val = entry.metadata.payload?.[key]
  return typeof val === 'string' ? val : ''
}

function levelChipClass(classes: Classes, level: LogLevel): string {
  switch (level) {
    case 'ERROR':
      return `${classes.levelChip} ${classes.levelChipError}`
    case 'WARN':
      return `${classes.levelChip} ${classes.levelChipWarn}`
    case 'INFO':
      return `${classes.levelChip} ${classes.levelChipInfo}`
    case 'DEBUG':
      return `${classes.levelChip} ${classes.levelChipDebug}`
    default:
      return classes.levelChip
  }
}

// Column model for the log table. The same definitions drive both the header
// row and the body cells, so reworking columns later means editing this list
// in one place. Cells reproduce the prior hand-rolled markup verbatim; sorting
// and pagination stay server-side (manualSorting — see useLogViewer), so no
// row models are wired up. Column ids must match the SortField values the
// query builders understand. Sortable columns need an accessorFn even though
// sorting is manual — TanStack's getCanSort() treats accessor-less display
// columns as unsortable and the header would render without its sort button.
// Text columns sort A→Z on first click; Timestamp starts at newest-first,
// matching the default view.
export function buildLogColumns(
  classes: Classes,
  searchTerm: string,
  t: Translate,
): Array<ColumnDef<LogEntry>> {
  return [
    {
      id: 'timestamp',
      accessorFn: row => row.timestamp,
      header: t('log_viewer.column.timestamp'),
      size: 145,
      minSize: 90,
      sortDescFirst: true,
      cell: ({ row }) => (
        <div className={classes.logTimestamp}>
          {formatTimestamp(row.original.timestamp)}
        </div>
      ),
    },
    {
      id: 'level',
      accessorFn: row => row.level,
      header: t('log_viewer.column.level'),
      size: 60,
      minSize: 48,
      sortDescFirst: false,
      cell: ({ row }) => (
        <div>
          <span className={levelChipClass(classes, row.original.level)}>
            {row.original.level}
          </span>
        </div>
      ),
    },
    {
      id: 'source',
      accessorFn: row => row.subsystem,
      header: t('log_viewer.column.source'),
      size: 110,
      minSize: 70,
      sortDescFirst: false,
      cell: ({ row }) => {
        const label = formatSubsystem(row.original.subsystem)
        return (
          <div
            className={classes.logSource}
            title={label}
          >
            {label}
          </div>
        )
      },
    },
    {
      id: 'machine',
      accessorFn: row => getPayloadField(row, 'MachineName'),
      header: t('log_viewer.column.machine'),
      size: 95,
      minSize: 60,
      sortDescFirst: false,
      cell: ({ row }) => {
        const value = getPayloadField(row.original, 'MachineName')
        return (
          <div
            className={classes.logMachine}
            title={value}
          >
            {value !== '' ? value : EM_DASH}
          </div>
        )
      },
    },
    {
      id: 'connection',
      accessorFn: row => getPayloadField(row, 'ConnectionId'),
      header: t('log_viewer.column.connection'),
      size: 105,
      minSize: 60,
      sortDescFirst: false,
      cell: ({ row }) => {
        const value = getPayloadField(row.original, 'ConnectionId')
        return (
          <div
            className={classes.logConnection}
            title={value}
          >
            {value !== '' ? value : EM_DASH}
          </div>
        )
      },
    },
    {
      id: 'state',
      accessorFn: row => getPayloadField(row, 'CommandType'),
      header: t('log_viewer.column.state'),
      size: 80,
      minSize: 55,
      sortDescFirst: false,
      cell: ({ row }) => {
        const value = getPayloadField(row.original, 'CommandType')
        return (
          <div
            className={classes.logState}
            title={value}
          >
            {value !== '' ? value : EM_DASH}
          </div>
        )
      },
    },
    {
      id: 'user',
      accessorFn: row => row.user ?? '',
      header: t('log_viewer.column.user'),
      size: 130,
      minSize: 60,
      sortDescFirst: false,
      cell: ({ row }) => <LogUserCell entry={row.original} />,
    },
    {
      id: 'message',
      accessorFn: row => row.description,
      header: t('log_viewer.column.message'),
      enableResizing: false,
      sortDescFirst: false,
      cell: ({ row }) => (
        <LogMessageCell
          entry={row.original}
          searchTerm={searchTerm}
        />
      ),
    },
  ]
}
